/**
 * Return the string with any single leading + trailing quote-like character
 * removed (straight " ' or curly " " ' '). Idempotent, safe on null/empty.
 *
 * We wrap queries/quoted snippets in typographic quotes in the UI
 * (`&ldquo;{text}&rdquo;`). If the text itself already has surrounding
 * quotes (e.g. an LLM emitted `"..."`), the naive wrap produces doubled
 * quotes (`""..." "`). This helper removes exactly one matched pair.
 */
export function stripSurroundingQuotes(s) {
  if (!s || typeof s !== "string") return s;
  const trimmed = s.trim();
  const openers = ['"', "'", "\u201C", "\u2018", "\u00AB"];
  const closers = ['"', "'", "\u201D", "\u2019", "\u00BB"];
  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  const openIdx = openers.indexOf(first);
  if (openIdx === -1) return trimmed;
  if (last !== closers[openIdx]) return trimmed;
  return trimmed.slice(1, -1).trim();
}


function _escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Split a string into an array of { text, match } segments, marking any
 * substrings that case-insensitively match one of the given terms.
 *
 * - Terms are matched longest-first so "New York" beats "New".
 * - Empty / whitespace-only terms are ignored.
 * - Preserves the original casing of the source string in output.
 * - Segments returned in-order; safe to render as React children.
 *
 * @param {string} source
 * @param {string[]} terms
 * @returns {{ text: string, match: boolean }[]}
 */
export function highlightTerms(source, terms) {
  if (!source || typeof source !== "string") return [{ text: source || "", match: false }];
  const clean = (terms || [])
    .map((t) => (t || "").trim())
    .filter((t) => t.length >= 2)
    .sort((a, b) => b.length - a.length);
  if (clean.length === 0) return [{ text: source, match: false }];

  const pattern = new RegExp(`(${clean.map(_escapeRegex).join("|")})`, "gi");
  const parts = [];
  let last = 0;
  for (const m of source.matchAll(pattern)) {
    if (m.index > last) parts.push({ text: source.slice(last, m.index), match: false });
    parts.push({ text: m[0], match: true });
    last = m.index + m[0].length;
  }
  if (last < source.length) parts.push({ text: source.slice(last), match: false });
  return parts;
}

